import { useMemo } from 'react';
import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query';

import { readShigoAppConfig } from '../../../lib/config';
import { useShigoSession } from '../../../lib/useShigoSession';
import { createDemandPartnersClient } from './api';
import type { DemandPartnerTargetingList } from './model';
import { type DemandPartnerMutationPayload, type DemandPartnersFilter } from './model';

export const demandPartnersQueryKey = ['demand-partners'] as const;
export const demandPartnersListsQueryKey = [...demandPartnersQueryKey, 'list'] as const;
export const demandPartnerOptionsQueryKey = [...demandPartnersQueryKey, 'options'] as const;
export const demandPartnerDetailsQueryKey = [...demandPartnersQueryKey, 'detail'] as const;
export const demandPartnerStatusCountsQueryKey = [
  ...demandPartnersQueryKey,
  'status-counts',
] as const;

export function useDemandPartnersClient() {
  const { session } = useShigoSession();
  const config = readShigoAppConfig();
  if (!session) throw new Error('Shigo session is required.');
  return useMemo(
    () =>
      createDemandPartnersClient({
        config: { apiBaseUrl: config.apiBaseUrl, networkId: config.networkId },
        accessToken: session.accessToken,
      }),
    [config.apiBaseUrl, config.networkId, session.accessToken],
  );
}

export function useDemandPartnersQuery(filter: DemandPartnersFilter) {
  const client = useDemandPartnersClient();
  return useQuery({
    queryKey: [...demandPartnersListsQueryKey, filter],
    queryFn: () => client.list(filter),
  });
}

export function useDemandPartnerOptionsQuery(searchTerm: string, selectedId?: string) {
  const client = useDemandPartnersClient();
  const normalizedSearch = searchTerm.trim();
  const hydrateId = normalizedSearch ? undefined : selectedId;
  return useQuery({
    queryKey: [...demandPartnerOptionsQueryKey, normalizedSearch, hydrateId ?? ''],
    queryFn: () => client.search(normalizedSearch, hydrateId),
    enabled: Boolean(normalizedSearch || hydrateId),
  });
}

export function useDemandPartnerStatusCountsQuery(searchTerm: string) {
  const client = useDemandPartnersClient();
  return useQuery({
    queryKey: [...demandPartnerStatusCountsQueryKey, searchTerm],
    queryFn: () => client.getStatusCounts({ searchTerm }),
  });
}

export function useDemandPartnerOwnersQuery() {
  const client = useDemandPartnersClient();
  return useQuery({
    queryKey: [...demandPartnersQueryKey, 'owners'],
    queryFn: () => client.listOwners(),
  });
}

export function useDemandPartnerBidderConfigQuery() {
  const client = useDemandPartnersClient();
  return useQuery({
    queryKey: [...demandPartnersQueryKey, 'bidder-config'],
    queryFn: () => client.getBidderConfig(),
  });
}

export function useDemandPartnerQuery(id: string) {
  const client = useDemandPartnersClient();
  return useQuery({
    queryKey: [...demandPartnerDetailsQueryKey, id],
    queryFn: () => client.get(id),
    enabled: Boolean(id),
    refetchOnMount: 'always',
  });
}

export function useDemandPartnerMutations() {
  const client = useDemandPartnersClient();
  const queryClient = useQueryClient();
  const invalidateChangedPartnerData = () =>
    Promise.all([
      queryClient.invalidateQueries({ queryKey: demandPartnersListsQueryKey }),
      queryClient.invalidateQueries({ queryKey: demandPartnerStatusCountsQueryKey }),
      queryClient.invalidateQueries({
        queryKey: demandPartnerOptionsQueryKey,
        refetchType: 'none',
      }),
    ]);
  return {
    create: useMutation({
      mutationFn: (payload: DemandPartnerMutationPayload) => client.create(payload),
      onSuccess: invalidateChangedPartnerData,
    }),
    update: useMutation({
      mutationFn: ({ id, payload }: { id: string; payload: DemandPartnerMutationPayload }) =>
        client.update(id, payload),
      onSuccess: invalidateChangedPartnerData,
    }),
    downloadTargetingListTemplate: useMutation({
      mutationFn: (list: DemandPartnerTargetingList) => client.downloadTargetingListTemplate(list),
    }),
    downloadTargetingList: useMutation({
      mutationFn: ({ id, list }: { id: string; list: DemandPartnerTargetingList }) =>
        client.downloadTargetingList(id, list),
    }),
    // react-doctor-disable-next-line react-doctor/query-mutation-missing-invalidation -- editor shows the returned count; detail refetches on every mount, so no stale cache is shown
    uploadTargetingList: useMutation({
      mutationFn: ({
        id,
        list,
        file,
      }: {
        id: string;
        list: DemandPartnerTargetingList;
        file: File;
      }) => client.uploadTargetingList(id, list, file),
    }),
  };
}
